import React, { useState, useEffect } from 'react';
import { useRouteData, useSiteData, Head } from 'react-static';
import LazyLoad from 'react-lazyload';
// template components
import ContentHub from '@nmx/template/dist/componentLibrary/modules/ContentHub';
import InPageLeadForm from '@nmx/template/dist/componentLibrary/modules/InPageLeadForm';
import Page from '@nmx/template/dist/componentLibrary/layout/Page';
import Theme from '@nmx/template/dist/componentLibrary/Theme';
// native components
import AdvisorConnectModule from './components/advisorConnectModule';
import CredentialCalloutGrouping from './components/credentialCalloutGrouping';
import ExperienceCallout from './components/experienceCallout';
import Faq from './components/faq';
import FinancialPlanningSection from './components/financialPlanningSection';
import HeroSection from './components/heroSection';
import InPageLeadFormLazyLoadContainer from '../../components/inPageLeadFormLazyLoadContainer';
import IntroSection from './components/introSection';
import Metadata from './components/metadata';
import ProductCalloutGrouping from './components/productCalloutGrouping';
import QuizCallout from './components/quizCallout';
import SectionVideoPlayer from './components/sectionVideoPlayerSection';
// images
import highResDesktopImage from './components/heroSection/images/di-hero-desktop.webp';
import highResMobileImage from './components/heroSection/images/di-hero-mobile.webp';
// dislaimer footnotes
import {
  DividendsAreNotGuaranteed,
  GlobalMarketIntelligenceForDisability,
  LoyaltyBasedOnClientData,
} from '../../../utils/disclaimerFootnotes';
// other utils
import { getImmediateMatchFeatureFlag } from '../../../utils/getFeatureFlags/getFeatureFlags';

export default () => {
  const { config, dynamicConfig } = useSiteData();
  const { pageProps, contentHubItems } = useRouteData();
  // We don't want to load recaptcha on page load (for performance reasons) so we will load it once the in page lead for is engaged with
  const [loadRecaptcha, setLoadRecaptcha] = useState(false);
  const [immediateMatchFeatureFlag, setImmediateMatchFeatureFlag] = useState(false);

  useEffect(() => {
    getImmediateMatchFeatureFlag(setImmediateMatchFeatureFlag, config);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page
      {...pageProps} // defined in route.js
      headComponent={Head}
      config={config}
      dynamicConfig={dynamicConfig}
      useReCaptcha={loadRecaptcha}
      disableDisclaimerTopBorder
      breadcrumbsNavigationConfig={[
        {
          pageName: 'Home',
          linkUrl: '/',
          linkAriaLabel: 'Northwestern Mutual Home',
        },
        {
          pageName: 'Insurance',
          linkUrl: '/insurance/',
          linkAriaLabel: 'Northwestern Mutual Insurance',
        },
        {
          pageName: 'Disability Insurance',
          linkUrl: '/disability-insurance/',
          linkAriaLabel: 'Northwestern Mutual Disability Insurance',
        },
      ]}
      disclaimerFootnotes={[
        <>
          <sup>1</sup>&nbsp;Based on today&#39;s 20&#45;year&#45;olds. U.S. Social Security Administration Fact Sheet, December 2023.
        </>,
        <>
          <sup>2</sup>&nbsp;An average Northwestern Mutual individual disability income insurance policy (Form ICC16.TT.NCDI.(0916)) with a &#36;2,000 monthly benefit costs males &#36;25.63&#47;month and females &#36;36.68&#47;month. Typical coffee costs &#36;72.52&#47;month. Eligibility for individual disability income insurance, additional policy benefits and qualifications for benefits is subject to underwriting and determined on a case&#45;by&#45;case basis.
        </>,
        <>
          {LoyaltyBasedOnClientData(3)}
        </>,
        <>
          {GlobalMarketIntelligenceForDisability(4)}
        </>,
        <>
          {DividendsAreNotGuaranteed(5)}
        </>,
      ]}
      customDisclaimers={[
        <>
          To be used with form ICC16.TT.DI.IIB.(0916), ICC16.TT.DI.FIB.(0916), ICC16.TT.DI.CAT.(0916), ICC16.TT.NCDI.(0916), ICC16.TT.GRDI.(0916), ICC16.TT.DI.PDB.(0916), ICC16.TT.DI.PDBO.(0916) or state equivalent. Not all contracts and optional benefits are available in all states.
        </>,
        <>
          Disability income insurance policies contain some contractual features and optional benefits that may not available in all states. The ability to perform the substantial and material duties of your occupation is only one of the factors that determine eligibility for disability benefits. These policies also contain exclusions, limitations and reduction&#45;of&#45;benefit provisions. Eligibility for disability income insurance, additional policy benefits and qualification for benefits is determined on a case&#45;by&#45;case basis. For costs and complete details of coverage, please contact a Northwestern Mutual financial representative.
        </>,
      ]}
      stateFilingComplianceNumber={<>90&#45;2688&#45;86 (0524)</>}

    >
      <Head>
        {/* prefetch expensive images */}
        <link rel='preload' href={highResMobileImage} as='image' media={`(max-width: ${Theme.breakpoints.values.medium}px)`} />
        <link rel='preload' href={highResDesktopImage} as='image' media={`(min-width: ${Theme.breakpoints.values.medium + 1}px)`} />
      </Head>

      <Metadata
        image={highResDesktopImage}
        pageDescription={pageProps.pageDescription}
        pageTitle={pageProps.pageTitle}
      />

      <HeroSection />

      <IntroSection />

      <SectionVideoPlayer />

      <QuizCallout />

      <Faq />

      {/* IN-PAGE LEAD FORM SECTION */}
      <InPageLeadFormLazyLoadContainer>
        <InPageLeadForm
          sectionId='in-page-lead' // note: id in-page-lead-form will be on the Form element itself.
          config={config}
          formLocation='disability-insurance'
          immediateMatchFeatureFlagEnabled={immediateMatchFeatureFlag}
          onFormEngaged={() => {
            if (!loadRecaptcha) {
              setLoadRecaptcha(true);
            }
          }}
          variant='embedded'
        />
      </InPageLeadFormLazyLoadContainer>

      <ProductCalloutGrouping config={config} />

      <FinancialPlanningSection />

      {/* EXPERIENCE CALLOUT */}
      <LazyLoad
        offset={200}
        height={300}
        once
        placeholder={
          <section id='disability-insurance-calc-experience-callout'>
            <h2>Disability Insurance Calculator</h2>
            <p>How much disability insurance do you need to protect your income&#63;</p>
          </section>
        }>
        <ExperienceCallout />
      </LazyLoad>

      <CredentialCalloutGrouping />

      <ContentHub
        sectionId='content-hub'
        headingText='Boost your disability insurance knowledge'
        contentHubItems={contentHubItems} // defined in route.js
        config={config}
      />

      <AdvisorConnectModule />

    </Page>
  );
};
